import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Newspaper, Plus, BadgeCheck, ArrowLeft } from "lucide-react";

const VIS_LABEL = { publico: "Público", global: "Global Natur Academy", franquicia: "Franquicia", profesor: "Profesor" };

export default function BlogPage() {
  const { user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [open, setOpen] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [form, setForm] = useState({ title_seo: "", meta_description: "", h1: "", content: "", url_slug: "", visibility: "global", category: "General", tags: "", status: "publicado", publish_at: "", featured: false, pin: "" });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const canCreate = ["SUPER_ADMIN", "FRANQUICIA", "PROFESOR"].includes(user.role);
  const canManage = (b) => user.role === "SUPER_ADMIN" || b.author_id === user.id;

  const loadComments = (bid) => api.get(`/blogs/${bid}/comments`).then((r) => setComments(r.data)).catch(() => {});
  const openBlog = (b) => { setOpen(b); loadComments(b.id); };

  const sendComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/blogs/${open.id}/comments`, { content: newComment });
      toast.success("Comentario enviado. Se publicará tras la aprobación del profesorado.");
      setNewComment(""); loadComments(open.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const patchBlog = async (body) => {
    try {
      const r = await api.patch(`/blogs/${open.id}`, body);
      setOpen(r.data); load();
      toast.success("Artículo actualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteBlog = async () => {
    const pin = window.prompt("PIN Maestro para eliminar el artículo:");
    if (!pin) return;
    try {
      await api.delete(`/blogs/${open.id}`, { headers: { "X-Master-Pin": pin } });
      toast.success("Artículo eliminado"); setOpen(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = () => api.get("/blogs").then((r) => setBlogs(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const draft = sessionStorage.getItem("natur_article_draft");
    if (draft) {
      const d = JSON.parse(draft);
      setForm((f) => ({ ...f, title_seo: d.seo_title || "", meta_description: d.meta_description || "", h1: d.h1 || "", url_slug: d.url_slug || "", content: d.content || "" }));
      setShowForm(true);
      sessionStorage.removeItem("natur_article_draft");
      toast.info("Borrador de Dakota cargado en el formulario");
    }
  }, []);

  const publish = async () => {
    try {
      const { pin, tags, publish_at, ...body } = form;
      const payload = { ...body, tags: tags.split(",").map((t) => t.trim()).filter(Boolean), publish_at: publish_at || null };
      const headers = { "X-Master-Pin": pin };
      if (editingBlog) await api.patch(`/blogs/${editingBlog}`, payload, { headers });
      else await api.post("/blogs", payload, { headers });
      toast.success(editingBlog ? "Artículo actualizado" : "Artículo guardado");
      setShowForm(false); setEditingBlog(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const startEdit = (b) => {
    setForm({ title_seo: b.title_seo, meta_description: b.meta_description, h1: b.h1, content: b.content,
              url_slug: b.url_slug, visibility: b.visibility, category: b.category || "General",
              tags: (b.tags || []).join(", "), status: b.status || "publicado", publish_at: b.publish_at || "",
              featured: !!b.featured, pin: "" });
    setEditingBlog(b.id); setOpen(null); setShowForm(true);
    window.scrollTo(0, 0);
  };

  if (open) {
    return (
      <article className="fade-up max-w-3xl" data-testid="blog-detail">
        <button data-testid="blog-back-btn" onClick={() => setOpen(null)} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-orange-700 mb-4">
          <ArrowLeft size={15} /> Volver al blog
        </button>
        <span className="text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-700 rounded-full px-2.5 py-1">{VIS_LABEL[open.visibility]}</span>
        <h1 className="text-3xl font-semibold tracking-tight mt-3">{open.h1}</h1>
        <p className="text-sm text-neutral-500 mt-2">
          {fmtDate(open.date)} · {open.author_name}
          {open.audited && <span className="inline-flex items-center gap-1 ml-2 text-green-700"><BadgeCheck size={14} /> Auditado por profesorado</span>}
        </p>
        <div className="mt-6 text-neutral-700 leading-relaxed whitespace-pre-wrap">{open.content}</div>
        {(open.tags || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {open.tags.map((t) => <span key={t} className="text-xs bg-neutral-100 text-neutral-600 rounded-full px-2.5 py-0.5">#{t}</span>)}
          </div>
        )}
        {canManage(open) && (
          <div className="mt-6 flex flex-wrap gap-2 border-t border-neutral-200 pt-4" data-testid="blog-admin-actions">
            <Button size="sm" className="rounded-full h-8 bg-orange-600 hover:bg-orange-700 text-white" data-testid="blog-edit-btn"
                    onClick={() => startEdit(open)}>
              Editar
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-8" data-testid="blog-toggle-hide-btn"
                    onClick={() => patchBlog({ status: (open.status || "publicado") === "oculto" ? "publicado" : "oculto" })}>
              {(open.status || "publicado") === "oculto" ? "Publicar" : "Ocultar"}
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-8" data-testid="blog-toggle-featured-btn"
                    onClick={() => patchBlog({ featured: !open.featured })}>
              {open.featured ? "Quitar destacado" : "Destacar"}
            </Button>
            {["SUPER_ADMIN", "FRANQUICIA"].includes(user.role) && (
              <Button size="sm" variant="outline" className="rounded-full h-8 border-red-300 text-red-600" data-testid="blog-delete-btn" onClick={deleteBlog}>
                Eliminar
              </Button>
            )}
          </div>
        )}
        <div className="mt-8 border-t border-neutral-200 pt-6" data-testid="blog-comments-section">
          <h2 className="font-semibold mb-4">Comentarios</h2>
          <div className="space-y-3 mb-4">
            {comments.map((c) => (
              <div key={c.id} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-4" data-testid="blog-comment">
                <p className="text-xs font-semibold text-orange-700">{c.user_name}
                  <span className="text-neutral-400 font-normal"> · {fmtDate(c.date)}</span>
                  {c.status !== "aprobado" && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{c.status}</span>}
                </p>
                <p className="text-sm mt-1">{c.content}</p>
              </div>
            ))}
            {comments.length === 0 && <p className="text-sm text-neutral-500">Sé el primero en comentar.</p>}
          </div>
          <div className="flex gap-2">
            <Input data-testid="blog-comment-input" placeholder="Escribe un comentario (requiere aprobación)…" value={newComment}
                   onChange={(e) => setNewComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()} />
            <Button data-testid="blog-comment-btn" onClick={sendComment} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white shrink-0">Comentar</Button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <div className="fade-up" data-testid="blog-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Blog</h1>
          <p className="text-neutral-500 mt-1">Artículos educativos, auditados por profesorado de Natur Autoescuela.</p>
        </div>
        {canCreate && (
          <Button data-testid="blog-add-btn" onClick={() => { setEditingBlog(null); setShowForm(!showForm); }} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
            <Plus size={15} className="mr-1.5" /> Añadir Blog
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 mb-8 space-y-3" data-testid="blog-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Input data-testid="blog-title-input" placeholder="Título SEO (50-60 caracteres)" value={form.title_seo}
                     onChange={(e) => setForm({ ...form, title_seo: e.target.value })} />
              <p className="text-xs text-neutral-400 mt-1">{form.title_seo.length} caracteres</p>
            </div>
            <div>
              <Input data-testid="blog-h1-input" placeholder="H1 (20-70 caracteres)" value={form.h1}
                     onChange={(e) => setForm({ ...form, h1: e.target.value })} />
              <p className="text-xs text-neutral-400 mt-1">{form.h1.length} caracteres</p>
            </div>
          </div>
          <div>
            <Textarea data-testid="blog-meta-input" placeholder="Meta descripción (140-160 caracteres)" rows={2} value={form.meta_description}
                      onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
            <p className="text-xs text-neutral-400 mt-1">{form.meta_description.length} caracteres</p>
          </div>
          <Textarea data-testid="blog-content-input" placeholder="Artículo (1800-2200 palabras recomendadas)" rows={8} value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })} />
          <div className="grid md:grid-cols-3 gap-3">
            <Input data-testid="blog-slug-input" placeholder="URL (menos de 60 caracteres)" value={form.url_slug}
                   onChange={(e) => setForm({ ...form, url_slug: e.target.value })} />
            <select data-testid="blog-visibility-select" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                    className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
              <option value="publico">Público</option>
              <option value="global">Registrados · Global</option>
              <option value="franquicia">Registrados · Franquicia</option>
              <option value="profesor">Registrados · Profesor</option>
            </select>
            <Input data-testid="blog-pin-input" placeholder="PIN Maestro" type="password" value={form.pin}
                   onChange={(e) => setForm({ ...form, pin: e.target.value })} />
            <Input data-testid="blog-category-input" placeholder="Categoría (ej. Normativa)" value={form.category}
                   onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <Input data-testid="blog-tags-input" placeholder="Etiquetas separadas por comas" value={form.tags}
                   onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            <select data-testid="blog-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
              <option value="publicado">Publicar ahora</option>
              <option value="borrador">Guardar como borrador</option>
              <option value="programado">Programar publicación</option>
              <option value="oculto">Oculto</option>
            </select>
            {form.status === "programado" && (
              <Input data-testid="blog-publishat-input" type="datetime-local" value={form.publish_at}
                     onChange={(e) => setForm({ ...form, publish_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="blog-featured-checkbox" checked={form.featured}
                     onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-orange-600" />
              Destacar artículo
            </label>
          </div>
          <Button data-testid="blog-publish-btn" onClick={publish} className="rounded-full bg-neutral-900 text-white">
            {editingBlog ? "Guardar cambios" : "Publicar artículo"}
          </Button>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {blogs.map((b) => (
          <button key={b.id} onClick={() => openBlog(b)} data-testid="blog-card"
                  className="text-left bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-transform">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-700 rounded-full px-2.5 py-0.5">{VIS_LABEL[b.visibility]}</span>
              {b.featured && <span className="text-[10px] font-semibold uppercase tracking-wider bg-amber-400 text-white rounded-full px-2.5 py-0.5">★ Destacado</span>}
              {b.status && b.status !== "publicado" && <span className="text-[10px] font-semibold uppercase tracking-wider bg-neutral-200 text-neutral-600 rounded-full px-2.5 py-0.5">{b.status}</span>}
              {b.category && <span className="text-[10px] text-neutral-400 uppercase tracking-wider">{b.category}</span>}
            </div>
            <h3 className="font-semibold text-lg leading-snug mt-3">{b.h1}</h3>
            <p className="text-sm text-neutral-500 mt-2 line-clamp-3">{b.meta_description}</p>
            <p className="text-xs text-neutral-400 mt-3">{fmtDate(b.date)} · {b.author_name}</p>
          </button>
        ))}
        {blogs.length === 0 && <p className="text-sm text-neutral-500">Todavía no hay artículos visibles para ti.</p>}
      </div>
    </div>
  );
}
